/**
 * Release Run Pane Header
 *
 * @module features/releases/components/ReleaseRunPaneHeader
 * @description Slim header for the timeline's detail pane (`ReleaseRunPane`).
 * The selected band already shows the run's identity (status node, short
 * SHA, title, regression count, commit count) and runs physically
 * into this pane, so the header repeats none of it — it carries only what
 * the band doesn't: the git range and the run's duration, plus Close.
 */

import { Clock, GitBranch, X } from '@phosphor-icons/react';
import { Button, Text } from '@chatsdet/ui';
import type { ReleaseRunDetail } from '../releaseTypes';
import { ReleaseRunGitRange } from './ReleaseRunGitRange';

/** "58m", "8h 59m" — compact, unlike date-fns' `formatDuration` ("8 hours 59 minutes 19 seconds"). Seconds are dropped: too precise to be useful once a run has been going for any length of time. */
function formatCompactDuration(startedAt: string, finishedAt: string | null): string {
  const start = new Date(startedAt).getTime();
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  const totalMinutes = Math.max(0, Math.round((end - start) / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

export function ReleaseRunPaneHeader({ run, onClose }: { run: ReleaseRunDetail; onClose: () => void }) {
  const durationStr = run.startedAt ? formatCompactDuration(run.startedAt, run.finishedAt) : null;

  return (
    <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border py-2 pl-4 pr-2">
      {/* Wraps rather than truncates so a long multi-repo compare range never
          hides behind the duration; it simply takes its own line. */}
      <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <GitBranch size={14} />
          <ReleaseRunGitRange run={run} />
        </span>
        {durationStr && (
          <span className="inline-flex items-center gap-1.5">
            <Clock size={14} />
            <Text size="sm">{durationStr}</Text>
          </span>
        )}
      </div>
      <Button variant="icon" size="sm" iconOnly tooltip="Close" onPress={onClose} className="shrink-0">
        <X size={16} />
      </Button>
    </div>
  );
}
